import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import HeroSection from "../components/HeroSection";
import TrustBar from "../components/TrustBar";
import BrandMarquee from "../components/BrandMarquee";
import ProductCard from "../components/ProductCard";
import Categories from "../components/Categories";
import StoreSection from "../components/StoreSection";
import { useGet } from "../api/useApi";
import { API_ROUTES } from "../api/apiRoutes";
import { buildQueryParams } from "../utils/pageHelpers";
import {
  CONDITION_FILTERS
} from "../data/constants";
import TopBarFilter from "../components/TopBarFilter";
import useSeo from "../seo/useSeo";
import { ROUTE_META } from "../seo/seoConfig";

const HomePage = ({ brandOptions }) => {
  useSeo({
    title: ROUTE_META["/"].title,
    description: ROUTE_META["/"].description,
    canonicalPath: "/",
  });

  const lastExecutedDeps = useRef(null); // Track what the dependencies were when we last executed
  const [filter, setFilter] = useState("all");
  const [productTagsList, setProductTagsList] = useState([]);

  const dynamicConditionFilters = [
    ...CONDITION_FILTERS,
    ...productTagsList.map((tag) => ({
      key: `tag_${tag.key}_${tag.label}`,
      label: tag.label,
    })),
  ];

  const {
    data: products,
    loading: loadingGetProducts,
    execute: getProducts,
  } = useGet(API_ROUTES.WATCHES.GET_ALL, {
    onError: (error) => {
      console.error("Error fetching products:", error);
    },
  });

  const { execute: getProductTags } = useGet(
    API_ROUTES.WATCHES.GET_PRODUCT_TAGS,
    {
      onSuccess: (data) => {
        const options = data?.value?.map((tag) => ({ key: tag?.id, label: tag?.name }));
        setProductTagsList(options || []);
      },
      onError: (error) => {
        console.error("Error fetching product tags:", error);
      },
    },
  );

  useEffect(() => {
    getProductTags();
  }, []);

  useEffect(() => {
    const currentDeps = JSON.stringify({
      filter,
    });

    // Skip if we've already executed with these exact values
    if (
      lastExecutedDeps.current !== null &&
      lastExecutedDeps.current === currentDeps
    ) {
      return;
    }

    lastExecutedDeps.current = currentDeps;

    getProducts({
      query: buildQueryParams({
        limit: 8,
        offset: 0,
        sort: "id%desc",
        ...(filter !== "latest" ? handleQueryFilter(filter) : {}),
      }),
    });
  }, [filter, getProducts]);

  const handleQueryFilter = (value) => {
    if (value?.startsWith("tag_")) {
      // key format: "tag_{tag.key}_{tag.label}"
      const withoutPrefix = value?.replace(/^tag_/, "");
      const underscoreIndex = withoutPrefix?.indexOf("_");
      const tagKey = withoutPrefix?.substring(0, underscoreIndex);
      return { tag_id: tagKey };
    }

    switch (value) {
      case "men/unisex":
        return { gender: "Men's/Unisex" };
      case "women":
        return { gender: "Women's" };
      case "new":
        return { condition: "New" };
      case "pre-owned":
        return { condition: "Pre-owned" };
      default:
        return {};
    }
  };

  return (
    <>
      <HeroSection />
      <TrustBar />
      <BrandMarquee brandOptions={brandOptions} />

      {/* Our Collections */}
      <section id="collections">
        <div className="section-header">
          <div className="section-label">Curated Selection</div>
          <h2 className="section-title">Our Collections</h2>
          <div className="section-line" />
        </div>
        <div className="all-watches-filters">
          <TopBarFilter
            filters={dynamicConditionFilters}
            filter={filter}
            handleFilterChange={(newFilter) => setFilter(newFilter)}
          />
        </div>
        {loadingGetProducts ? (
          <div className="spinner-container">
            <div className="spinner" />
          </div>
        ) : (
          <div className="product-grid">
            {products?.value?.map((w, i) => (
              <ProductCard key={w?.id} watch={w} index={i} />
            ))}
          </div>
        )}
        <div className="view-more-wrap">
          <Link
            to="/watches"
            className="view-more"
            style={{ textDecoration: "none", display: "inline-block" }}
          >
            View All Watches
          </Link>
        </div>
      </section>

      <Categories />
      <StoreSection />
    </>
  );
};

export default HomePage;
